import { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { NextPage } from 'next';
import { maxBy, throttle } from 'lodash';

import Link from 'next/link';

import { i18n as I18n, withTranslation } from 'utils/i18n';
import { filteredVenueDiscounts } from 'utils/filters';

// components

import Page from 'components/page';
import VenueContentBlock from 'components/venueContentBlock';
import VenueContentBlockItem from 'components/venueContentBlockItem';
import SlideShow from 'components/shared/slideShow';
import VenueSpaces from 'components/venueSpaces';
import QuickBook from 'components/quickBook/index';
import BookingTimes from 'components/bookingTimes';
import StickyPanel from 'components/stickyPanel';
import FloorplansButton from 'components/floorplansButton';
import VenueHeader from 'components/venueHeader';
import NearbyVenues from 'components/nearbyVenues';
import Discount from 'components/discount';
import DiscountTermsModal from 'components/discountTermsModal';

// hooks
import useStoredBooking from 'hooks/useStoredBooking';
import useDataLayer from 'hooks/useDataLayer';
import useVenue from 'hooks/useVenue';
import useNearbyVenues from 'hooks/useNearbyVenues';
import usePropertyDiscount from 'hooks/usePropertyDiscount';

import { useRouter } from 'next/router';
import { SearchContext } from 'state/search';

const PropertyPage: NextPage = () => {
  const router = useRouter();
  const { id } = router.query;

  const venueId = Number.isNaN(Number(id))
    ? Number(id.split('-')['0'])
    : Number(id);

  const { data: venue = {}, loading } = useVenue(venueId, true);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [displayStickyNav, toggleStickyPanel] = useState(true);

  const {
    filters: { date: searchDate },
  } = useContext(SearchContext);

  useEffect(() => {
    const { view, ...query } = router.query;
    if (!!view && !!venue)
      router.replace({ pathname: router.pathname, query }, undefined, {
        scroll: false,
        shallow: true,
      });
  }, [router, venue]);

  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] = useState(
    false
  );

  const quickBookRef = useRef({});

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (space, venue) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue.id) {
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...storedSpaceIds, space.id],
        date: searchDate || storedBooking.date || new Date(),
      }));
    }

    quickBookRef.current.scrollIntoView({ behaviour: 'smooth' });
    // adding as .scrollIntoView above doesn't scroll enough due to sticky header
    window.scrollBy(0, -120);
  };

  const venueDescription = () => {
    return (
      venue.description &&
      venue.description
        .replace(/\n/g, '<br />')
        .replace(/(<? *script)/gi, 'illegalscript')
    );
  };

  const spaceListRef = useRef();
  const hasAnchor = useMemo(
    () =>
      !router.asPath && loading ? false : router.asPath.split('#').length > 1,
    [router, loading]
  );

  useEffect(() => {
    const handleScroll = () => {
      if (spaceListRef.current) {
        const bounds = spaceListRef.current.getBoundingClientRect();

        if (bounds.bottom > window.innerHeight) {
          toggleStickyPanel(true);
        } else {
          toggleStickyPanel(false);
        }
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 500);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  useDataLayer(
    venue && venue.id
      ? {
          event: 'ospace.property',
          location: venue.address ? venue.address.city : null,
          venueId: venue.id,
          venueName: venue.name,
        }
      : null
  );

  const {
    images: { regular: images = [] } = {},
    facets = [],
    clinicalRooms = [],
    nonClinicalRooms = [],
    organisation = { name: '', largeIcon: '', url: '' },
  } = { ...(loading ? {} : venue) };

  const { discounts = [] } = usePropertyDiscount({
    venueIds: [venueId],
  });

  const venueDiscounts = filteredVenueDiscounts(discounts, venue);

  const { data: venues = [] } = useNearbyVenues({
    venueId,
  });

  if (!venue) {
    return null;
  }

  return (
    <Page
      title={I18n.t('headers.property_page.title', {
        name: venue.name,
      })}
      description={venue.description}
    >
      <main className="venues">
        <section>
          <div className="section-content venue-content" ref={spaceListRef}>
            <VenueHeader
              name={venue.name}
              fullAddress={venue.fullAddress}
              organisation={organisation}
            />

            <div className="layout layout--two-col-stacked-left venues">
              <div className="layout__first">
                <div className="venues__slideshow" id="slideShow">
                  {venueDiscounts.length > 0 && (
                    <>
                      <Discount
                        text={I18n.t('discounts.save_up_to', {
                          percent: maxBy(
                            venueDiscounts,
                            discount => discount.amount
                          ).amount,
                        })}
                        rotated
                        size="large"
                        onClick={() => setDiscountTermsModalIsOpen(true)}
                      />

                      <DiscountTermsModal
                        isOpen={discountTermsModalIsOpen}
                        onClose={() => setDiscountTermsModalIsOpen(false)}
                        propertyOrRoomName={venue.name}
                        discounts={venueDiscounts}
                      />
                    </>
                  )}

                  {venue.address && (
                    <SlideShow slideUrls={images} mapLatLng={venue.latLng} />
                  )}

                  <FloorplansButton venue={venue} />
                </div>
                <div className="venues__summary">
                  {/* eslint-disable react/no-danger */}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: venueDescription(),
                    }}
                  />
                  {/* eslint-enable react/no-danger */}
                </div>
                <BookingTimes
                  bookingSlots={venue.bookingSlots}
                  openingTimes={venue.nonClinicalOpeningTimes}
                  clinicalRooms={clinicalRooms.length > 0}
                  nonClinicalRooms={nonClinicalRooms.length > 0}
                  text={I18n.t('venues.booking_times_text')}
                />
                <StickyPanel show={false}>
                  <Link
                    href={`/properties/${venue.id}#venue-rooms-section`}
                    to={{
                      pathname: `/properties/${venue.id}`,
                    }}
                  >
                    <button className="button button--primary button--block">
                      {I18n.t('venues.book_rooms')}
                    </button>
                  </Link>
                </StickyPanel>
              </div>
              <div className="layout__last">
                <div className="venues__book-now-button">
                  <Link
                    href={`/properties/${venue.id}#venue-rooms-section`}
                    to={{
                      pathname: `/properties/${venue.id}`,
                    }}
                  >
                    <button className="button button--primary button--block">
                      {I18n.t('venues.book_rooms')}
                    </button>
                  </Link>
                </div>
                {facets.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock title={I18n.t('shared.facilities')}>
                      {facets.map(facet => (
                        <VenueContentBlockItem
                          key={facet.name}
                          title={facet.name}
                          icon={facet.icon}
                          iconAltText={facet.iconAltText}
                          displayCount={false}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                {clinicalRooms.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock title={I18n.t('venues.clinical_rooms')}>
                      {clinicalRooms.map(room => (
                        <VenueContentBlockItem
                          key={room.name}
                          count={room.count}
                          title={room.name}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                {nonClinicalRooms.length > 0 && (
                  <ul className="venue-content-block__block">
                    <VenueContentBlock
                      title={I18n.t('venues.non_clinical_rooms')}
                    >
                      {nonClinicalRooms.map(room => (
                        <VenueContentBlockItem
                          key={room.name}
                          count={room.count}
                          title={room.name}
                        />
                      ))}
                    </VenueContentBlock>
                  </ul>
                )}
                <ul className="venue-content-block__block">
                  <VenueContentBlock
                    title={I18n.t('venues.property_contact_details')}
                  >
                    <ul className="contact-details__list">
                      <li className="contact-details__wrap">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/phone.svg"
                            alt={I18n.t('venues.contact_methods.telephone')}
                          />
                        </div>
                        <a href={`tel:${venue.telephone}`}>{venue.telephone}</a>
                      </li>
                      <li className="contact-details__wrap contact-details__email">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/email.svg"
                            alt={I18n.t('venues.contact_methods.email')}
                          />
                        </div>
                        <a href={`mailto:${venue.contactEmail}`}>
                          {venue.contactEmail}
                        </a>
                      </li>
                      <li className="contact-details__wrap">
                        <div className="contact-details__icon">
                          <img
                            src="/images/icons/rooms-properties/help-faq.svg"
                            alt={I18n.t(
                              'property.contact_details.help_and_faq'
                            )}
                          />
                        </div>
                        <Link href={I18n.t('navigation.help.url')}>
                          <a href={I18n.t('navigation.help.url')}>
                            {I18n.t('property.contact_details.help_and_faq')}
                          </a>
                        </Link>
                      </li>
                    </ul>
                  </VenueContentBlock>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {venue && venues.length > 0 && venue.address && (
          <NearbyVenues location={venue.address.postcode} venues={venues} />
        )}

        <section
          className="highlighted"
          id="venue-rooms-section"
          data-cy="venueRoomsList"
        >
          <div className="section-content">
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                {typeof window !== 'undefined' ? (
                  <VenueSpaces
                    venue={venue}
                    selectedVenueId={storedVenueId}
                    selectedSpaceIds={storedSpaceIds}
                    onAddToBooking={selectSpace}
                    scrollIntoView={!loading && hasAnchor}
                  />
                ) : null}
              </div>
              <div className="layout__last">
                <QuickBook ref={quickBookRef} />
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

// PropertyPage.getInitialProps = async () => {
//   return {
//     namespacesRequired: ['common'],
//   };
// };

export default withTranslation('common')(PropertyPage);
